import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { CarouselRef } from 'antd/lib/carousel';
import { RiskAnalysisCont, RiskSliderCont } from './RiskAnalysis.styles';
import { useFilters } from '../../hooks/useFilters';
import useRiskAnalysis from '../../hooks/useRiskAnalysis';
import { useScroll } from '../../hooks/useScroll';
import { IPsychosocialRisk } from '../../services/reports';
import ReportResultsLegend from '../ReportResultsLegend/ReportResultsLegend';
import Slider from '../Slider/Slider';
import RiskAnalysisSlide from './RiskAnalysisSlide/RiskAnalysisSlide';

interface IRiskAnalysis {
  psychoSocialRisk: IPsychosocialRisk;
}

const RiskAnalysis: FC<IRiskAnalysis> = ({ psychoSocialRisk }) => {
  const { riskTransformedData } = useRiskAnalysis(psychoSocialRisk.results);
  const { applyFilters, currentFilters } = useFilters();
  const { isIntersecting, scrollToId } = useScroll('RiskAnalysisCont', '0% 0% 0% 0%');

  const [scrollToCheck, setScrollToCheck] = useState(true);
  const carouselRef = useRef<CarouselRef>(null);

  const { description } = psychoSocialRisk;

  const getPercentFilter = useMemo(() => {
    const riskAnalysis = currentFilters?.riskAnalysis;

    if (riskAnalysis) {
      // @ts-ignore
      const riskVal: any = JSON.parse(riskAnalysis);

      return {
        slide: riskVal?.slide || 0,
        legend: riskVal?.legend || false,
      };
    }
    return { slide: 0, legend: false };
  }, [currentFilters?.riskAnalysis]);

  const applyRiskFilter = (key: string, value: boolean | number) => {
    const riskAnalysis = currentFilters?.riskAnalysis;

    if (key === 'slide' && typeof value === 'number') {
      carouselRef.current?.goTo(value);
    }

    if (riskAnalysis) {
      // @ts-ignore
      const riskFilters = JSON.parse(riskAnalysis);
      const riskValue = JSON.stringify({ ...riskFilters, ...{ [key]: value } });
      return applyFilters({ riskAnalysis: riskValue });
    }
    const percentValue = JSON.stringify({ [key]: value });
    applyFilters({ riskAnalysis: percentValue });
  };

  useEffect(() => {
    const slide = getPercentFilter?.slide;
    if (slide && currentFilters?.riskAnalysis) {
      carouselRef.current?.goTo(slide);
    }

    if (scrollToCheck) {
      setScrollToCheck(false);
      if (currentFilters?.activeSection) {
        scrollToId('RiskAnalysisSlideCont');
      }
    }

    if (isIntersecting) {
      return applyFilters({ activeSection: 'RiskAnalysisCont' });
    }

    return applyFilters({ activeSection: null });
  }, [isIntersecting, scrollToCheck]);

  const sliderMockRender = useMemo(() => {
    return riskTransformedData?.map(result => (
      <RiskAnalysisSlide
        data={result}
        donutPartAction={(slideNo: number) => applyRiskFilter('slide', slideNo)}
        key={`analysis${result?.risk?.level}`}
      />
    ));
  }, [riskTransformedData, getPercentFilter]);

  return (
    <RiskAnalysisCont id="RiskAnalysisCont">
      <ReportResultsLegend
        variant="riskAnalysis"
        noBorder
        description={description}
        expandLegend={(status: boolean) => applyRiskFilter('legend', status)}
        legendExpanded={getPercentFilter?.legend}
      />
      <RiskSliderCont id="RiskSliderCont" height="auto">
        <Slider
          height="auto"
          slidesLength={3}
          activeSlide={getPercentFilter.slide}
          goToSlide={(slideNo: number) => applyRiskFilter('slide', slideNo)}
          carouselRef={carouselRef}
        >
          {sliderMockRender}
        </Slider>
      </RiskSliderCont>
    </RiskAnalysisCont>
  );
};

export default RiskAnalysis;
